import { useEffect, useState } from 'react';
import { AdminAccount, fetchAdminAccount, fetchAdminProfile } from '../api';
import { useAuth } from '../context/AuthContext';
import { Check, Copy, Link2, Loader2 } from 'lucide-react';
import EmailManager from './account/EmailManager';
import ChangePasswordCard from './account/ChangePasswordCard';

export default function AdminAccountSettings() {
  const { session, refresh } = useAuth();
  const [account, setAccount] = useState<AdminAccount | null>(null);
  const [shopUrl, setShopUrl] = useState('');
  const [urlCopied, setUrlCopied] = useState(false);
  const [loadingAccount, setLoadingAccount] = useState(true);

  useEffect(() => {
    // Keep the shared auth session fresh so EmailManager sees the right
    // verified state (the admin dashboard logs in outside the AuthContext).
    refresh();
    (async () => {
      try {
        const [data, profile] = await Promise.all([fetchAdminAccount(), fetchAdminProfile()]);
        setAccount(data);
        setShopUrl(`${window.location.origin}/shop/${profile.slug}`);
      } catch {
        /* ignore */
      }
      setLoadingAccount(false);
    })();
  }, [refresh]);

  const handleCopyUrl = async () => {
    if (!shopUrl) return;
    try {
      await navigator.clipboard.writeText(shopUrl);
      setUrlCopied(true);
      setTimeout(() => setUrlCopied(false), 2500);
    } catch {
      /* clipboard unavailable */
    }
  };

  return (
    <div className="space-y-6 sm:space-y-8">
      {/* ── Account Info ── */}
      <div className="border-default bg-surface overflow-hidden rounded-xl border shadow-sm">
        <div className="border-subtle border-b px-4 py-4 sm:px-6 sm:py-5">
          <h2 className="text-fg text-base font-bold sm:text-lg">Account Info</h2>
          <p className="text-fg-muted mt-0.5 hidden text-xs sm:block sm:text-sm">
            Your salon's public booking page
          </p>
        </div>
        <div className="px-4 py-4 sm:px-6">
          {loadingAccount ? (
            <div className="text-fg-subtle py-4 text-center">
              <Loader2 className="mx-auto mb-2 h-5 w-5 animate-spin" />
              Loading...
            </div>
          ) : (
            <dl className="divide-subtle divide-y">
              <div className="flex items-center gap-3 py-3 first:pt-0 last:pb-0">
                <Link2 className="text-fg-subtle h-5 w-5 shrink-0" />
                <dt className="text-fg-muted w-28 shrink-0 text-sm">Salon Page</dt>
                <dd className="flex min-w-0 flex-1 items-center gap-2">
                  {shopUrl ? (
                    <>
                      <a
                        href={shopUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-accent hover:text-accent min-w-0 flex-1 truncate text-sm font-medium hover:underline"
                      >
                        {shopUrl}
                      </a>
                      <button
                        type="button"
                        onClick={handleCopyUrl}
                        className="border-default text-fg-secondary hover:bg-canvas flex shrink-0 items-center gap-1 rounded-lg border px-2.5 py-1.5 text-xs font-medium transition-colors"
                      >
                        {urlCopied ? (
                          <>
                            <Check className="text-success h-3.5 w-3.5" />
                            Copied
                          </>
                        ) : (
                          <>
                            <Copy className="h-3.5 w-3.5" />
                            Copy
                          </>
                        )}
                      </button>
                    </>
                  ) : (
                    <span className="text-fg-subtle text-sm font-normal italic">—</span>
                  )}
                </dd>
              </div>
            </dl>
          )}
        </div>
      </div>

      {/* ── Email (view / change / verify) ── */}
      {!loadingAccount && (
        <EmailManager
          email={account?.email ?? ''}
          emailVerified={Boolean(session?.emailVerified)}
        />
      )}

      {/* ── Change password ── */}
      <ChangePasswordCard />
    </div>
  );
}
